import { Sparkles, Settings, LogOut, ChevronRight, Home } from "lucide-react";
import { Button } from "./components/ui/button";
import CostTrackerWidget from "./components/CostTrackerWidget";

/**
 * AppHeader — sticky top bar with logo, deep breadcrumb trail, and user actions.
 *
 * Breadcrumb crumb shape:
 *   { label: string, icon?: ReactNode, onClick?: () => void, testId?: string }
 *
 * A "Home" crumb (link to /select-platform) is prepended automatically unless
 * `hideHomeCrumb` is passed. The last crumb is rendered as the active page
 * (bolder, tinted chip) and is never clickable — even if `onClick` is set.
 */
const AppHeader = ({
  user,
  breadcrumbs = [],
  onNavigate,
  onLogout,
  rightContent,
  hideHomeCrumb = false,
}) => {
  // Auto-prepend Home crumb (unless suppressed). We skip it when the caller
  // already includes a crumb that goes to /select-platform (avoid duplication).
  const trail = (() => {
    if (hideHomeCrumb) return breadcrumbs;
    const alreadyHasHome = breadcrumbs.some(
      (c) => c.label?.toLowerCase() === "platforms" || c.label?.toLowerCase() === "home"
    );
    if (alreadyHasHome) return breadcrumbs;
    return [
      { label: "Home", icon: <Home className="w-3.5 h-3.5" />, onClick: () => onNavigate?.("/select-platform"), testId: "home" },
      ...breadcrumbs,
    ];
  })();

  return (
    <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50" data-testid="app-header">
      <div className="max-w-full mx-auto px-6 h-14 flex items-center justify-between gap-4">
        {/* Left: Logo + Breadcrumbs */}
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => onNavigate?.("/select-platform")}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity shrink-0 group"
            data-testid="horizon-logo"
            aria-label="Go to platforms home"
          >
            <div className="w-8 h-8 bg-[#32B4AC] rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900 hidden sm:block tracking-tight">HORIZON</span>
          </button>

          {/* Breadcrumbs */}
          {trail.length > 0 && (
            <nav
              className="flex items-center gap-0.5 pl-3 ml-1 border-l border-gray-200 min-w-0"
              aria-label="Breadcrumb"
              data-testid="breadcrumbs"
            >
              {trail.map((crumb, i) => {
                const isLast = i === trail.length - 1;
                const isClickable = !!crumb.onClick && !isLast;
                const testId = crumb.testId ? `breadcrumb-${crumb.testId}` : `breadcrumb-${i}`;
                const inner = (
                  <span className="inline-flex items-center gap-1.5 min-w-0">
                    {crumb.icon && (
                      <span className={`shrink-0 inline-flex ${isLast ? "text-[#32B4AC]" : "text-gray-400"}`}>
                        {crumb.icon}
                      </span>
                    )}
                    <span className="truncate">{crumb.label}</span>
                  </span>
                );
                return (
                  <div key={i} className="flex items-center gap-0.5 min-w-0">
                    {i > 0 && (
                      <ChevronRight
                        className="w-3.5 h-3.5 text-gray-300 shrink-0 mx-0.5"
                        aria-hidden="true"
                      />
                    )}
                    {isClickable ? (
                      <button
                        onClick={crumb.onClick}
                        className="text-[13px] font-medium text-gray-500 hover:text-[#32B4AC] hover:bg-[#F0FDFA] rounded-md px-2 py-1 transition-colors focus:outline-none focus:ring-2 focus:ring-[#32B4AC]/30 truncate max-w-[160px]"
                        data-testid={testId}
                        title={crumb.label}
                      >
                        {inner}
                      </button>
                    ) : (
                      <span
                        className={`text-[13px] px-2 py-1 rounded-md truncate max-w-[200px] ${
                          isLast
                            ? "font-semibold text-gray-900 bg-gray-50 border border-gray-150"
                            : "font-medium text-gray-500"
                        }`}
                        data-testid={testId}
                        aria-current={isLast ? "page" : undefined}
                        title={crumb.label}
                      >
                        {inner}
                      </span>
                    )}
                  </div>
                );
              })}
            </nav>
          )}
        </div>

        {/* Right: Custom content + user + actions */}
        <div className="flex items-center gap-2 shrink-0">
          {rightContent}
          <CostTrackerWidget />
          <div className="flex items-center gap-2 pl-3 border-l border-gray-200">
            {user?.picture && (
              <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
            )}
            <span className="text-sm text-gray-700 hidden sm:block">{user?.name}</span>
          </div>
          <div className="flex items-center gap-0.5 pl-2 border-l border-gray-200">
            <Button
              onClick={() => onNavigate?.("/settings")}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-gray-600"
              data-testid="header-settings-btn"
              title="Control Panel"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              onClick={onLogout}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-gray-600"
              data-testid="header-logout-btn"
              title="Log out"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
